/* eslint-disable no-console */

import { trackThenNavigate } from "utils/analytics"
import "./google-sign-in_large--white.scss"

const GOOGLE_SCRIPT_SRC = "https://accounts.google.com/gsi/client"
const SIGNUP_POPUP_FLAG = "signupPopupOpen"

let googleObserverStarted = false
let googleScriptLoading = false
let googleScriptReady = false
const googleScriptCallbacks = new Set<() => void>()
// GIS keeps one credential callback per page, so remember which button was used.
let lastClickedButton: HTMLElement | null = null

function getGoogle() {
  return (window as any).google
}

function isSignupPopupOpen() {
  return Boolean((window as any)[SIGNUP_POPUP_FLAG])
}

function dispatchSocialAuthSuccess(provider: string, detail: Record<string, unknown> = {}) {
  const event = new CustomEvent("social-auth:success", { detail: { provider, ...detail } })
  window.dispatchEvent(event)
}

// The touchpoint of the surface hosting the button: the sign-up popup, the
// login box or the sign-up page.
function touchpointFor(button: HTMLElement): string | undefined {
  return button.closest<HTMLElement>("[data-touchpoint]")?.dataset.touchpoint || undefined
}

function ensureGoogleScript(callback: () => void) {
  if (googleScriptReady || getGoogle()?.accounts?.id) {
    googleScriptReady = true
    callback()
    return
  }

  googleScriptCallbacks.add(callback)

  if (googleScriptLoading) {
    return
  }

  googleScriptLoading = true
  const script = document.createElement("script")
  script.src = GOOGLE_SCRIPT_SRC
  script.async = true
  script.defer = true
  script.onload = () => {
    googleScriptReady = true
    googleScriptLoading = false
    googleScriptCallbacks.forEach((cb) => cb())
    googleScriptCallbacks.clear()
  }
  document.head.appendChild(script)
}

function initGoogleSignInForButton(customBtnEl: HTMLElement) {
  if (customBtnEl.dataset.googleInit === "true") return
  customBtnEl.dataset.googleInit = "true"

  const clientId = customBtnEl.getAttribute("data-client-id") || ""
  const nonce = customBtnEl.getAttribute("data-nonce") || undefined
  const messageContainer = customBtnEl.parentElement || document.body

  let gisReady = false
  let pendingPrompt = false
  let popupFallbackUsed = false

  function showGoogleSignInMessage(message: string) {
    const existing = messageContainer.querySelector(".google-signin-message")
    const el = (existing || document.createElement("div")) as HTMLElement

    if (!existing) {
      el.className = "google-signin-message"
      el.setAttribute("role", "status")
      el.setAttribute("aria-live", "polite")
      el.style.marginTop = "0.5rem"
      el.style.fontSize = "0.875rem"
      customBtnEl.insertAdjacentElement("afterend", el)
    }

    el.textContent = message
  }

  const onCredential = (response: any) => {
    const credential = response?.credential
    if (!credential) {
      console.error("Sign in with Google failed: Missing credential")
      return
    }

    const touchpoint = touchpointFor(lastClickedButton || customBtnEl)

    fetch("/auth/google/handle_success", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token":
          document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "",
      },
      body: JSON.stringify({ credential, touchpoint }),
    })
      .then(async (res) => {
        let data: any = null
        try {
          data = await res.json()
        } catch (e) {
          data = null
        }

        if (res.ok) {
          const redirectUrl = data?.redirect_url || "/profile"
          const created = data?.created
          const navigate = () => {
            window.location.href = redirectUrl
          }
          if (isSignupPopupOpen()) {
            dispatchSocialAuthSuccess("google", { redirectUrl, created })
          } else if (typeof created === "boolean") {
            // Only sites whose controller reports account creation track it.
            trackThenNavigate(
              created ? "sign_up" : "login",
              { method: "google", touchpoint },
              navigate,
            )
          } else {
            navigate()
          }
        } else {
          console.error(`Sign in with Google failed: ${data?.message || "Please try again."}`)
        }
      })
      .catch((error) => {
        console.error(`An error occurred during sign in. Please try again. Error: ${error.message}`)
      })
  }

  const openGoogleLoginWindow = () => {
    const features = "width=500,height=600,menubar=0,toolbar=0,status=0"
    window.open(
      "https://accounts.google.com/signin/v2/identifier?continue=https%3A%2F%2Fwww.google.com%2F",
      "_blank",
      features,
    )
  }

  function initializeGsi(useFedcm: boolean) {
    const g = getGoogle()
    if (!g?.accounts?.id) return
    g.accounts.id.initialize({
      client_id: clientId,
      callback: onCredential,
      nonce,
      ux_mode: "popup",
      itp_support: true,
      use_fedcm_for_prompt: useFedcm,
      auto_select: false,
    })
  }

  function promptWithFallback(notificationHandler: (notification: any) => void) {
    const g = getGoogle()
    g?.accounts?.id?.prompt(notificationHandler)
  }

  function switchToPopupFallback(notificationHandler: (notification: any) => void) {
    if (popupFallbackUsed) return
    popupFallbackUsed = true
    initializeGsi(false)
    promptWithFallback(notificationHandler)
  }

  function handlePromptMoment(notification: any) {
    const notDisplayedReason = notification.getNotDisplayedReason?.()
    const skippedReason = notification.getSkippedReason?.()

    if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
      if (
        !popupFallbackUsed &&
        (notDisplayedReason === "opt_out_or_no_session" || !notDisplayedReason)
      ) {
        switchToPopupFallback(handlePromptMoment)
        return
      }

      if (notDisplayedReason === "opt_out_or_no_session") {
        openGoogleLoginWindow()
      } else {
        showGoogleSignInMessage(
          "Google sign-in could not start. Please allow popups and ensure you are signed in to your Google account.",
        )
      }
      if (notDisplayedReason || skippedReason) {
        console.warn("Google sign-in prompt skipped", { notDisplayedReason, skippedReason })
      }
    }
  }

  const ensureRendered = () => {
    try {
      const g = getGoogle()
      if (!g?.accounts?.id) return
      initializeGsi(true)
      gisReady = true
      const scopedContainer = messageContainer.querySelector("#googleid-signin")
      const container = (scopedContainer || document.getElementById("googleid-signin")) as
        | HTMLElement
        | null
      if (container) {
        g.accounts.id.renderButton(container, { theme: "outline", size: "large" })
      }

      if (pendingPrompt) {
        pendingPrompt = false
        promptWithFallback(handlePromptMoment)
      }
    } catch (e) {
      // noop
    }
  }

  const triggerGoogle = () => {
    const g = getGoogle()
    if (g?.accounts?.id && gisReady) {
      promptWithFallback(handlePromptMoment)
    } else {
      pendingPrompt = true
    }
  }

  customBtnEl.addEventListener("click", (e) => {
    e.preventDefault()
    lastClickedButton = customBtnEl
    triggerGoogle()
  })

  customBtnEl.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      lastClickedButton = customBtnEl
      triggerGoogle()
    }
  })

  ensureGoogleScript(ensureRendered)
}

function initGoogleSignIn() {
  const customButtons = Array.from(document.querySelectorAll("#custom-google-signin"))
  if (!customButtons.length) return
  customButtons.forEach((button) => initGoogleSignInForButton(button as HTMLElement))
}

function observeGoogleButtons() {
  if (!googleObserverStarted) {
    googleObserverStarted = true
    const observer = new MutationObserver(() => {
      initGoogleSignIn()
    })
    observer.observe(document.documentElement, { childList: true, subtree: true })
  }

  initGoogleSignIn()
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", observeGoogleButtons)
} else {
  observeGoogleButtons()
}
